import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINK =
  'font-montserrat font-light text-[17px] tracking-[0.07em] leading-none text-white hover:text-[#eebc02] transition-colors duration-300 cursor-pointer whitespace-nowrap';

const MOBILE_NAV_LINK =
  'font-montserrat font-light text-[18px] tracking-[0.07em] leading-none text-white hover:text-[#eebc02] transition-colors duration-300 cursor-pointer';

const MOBILE_SUB_LINK =
  'block font-montserrat font-light text-[16px] tracking-[0.07em] leading-relaxed text-white/80 hover:text-[#eebc02] transition-colors duration-300 cursor-pointer';

const DROPDOWN_ITEM_STYLE: React.CSSProperties = {
  display: 'block',
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 300,
  fontSize: '15px',
  letterSpacing: '0.05em',
  lineHeight: '1.5',
  color: '#ffffff',
  textDecoration: 'none',
  padding: '10px 24px',
  backgroundColor: 'transparent',
  border: 'none',
  outline: 'none',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'color 0.2s ease, background-color 0.2s ease',
};

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState<string | null>(null);
  const location = useLocation();
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isActive = (path: string) => location.pathname === path;
  const isActiveGroup = (paths: string[]) => paths.some(p => location.pathname.startsWith(p));

  const handleMouseEnter = (menu: string) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setOpenDropdown(menu);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => setOpenDropdown(null), 180);
  };

  const toggleMobileSubmenu = (menu: string) => {
    setOpenMobileSubmenu(openMobileSubmenu === menu ? null : menu);
  };

  const buyersSubmenu = [
    { label: "Buyers Guide", path: '/buyers-guide' },
    { label: 'Home Search', path: '/home-search' },
    { label: 'South Florida Relocation', path: '/south-florida-relocation' },
  ];

  const sellersSubmenu = [
    { label: "Sellers Guide", path: '/seller-guide' },
    { label: 'Home Valuation', path: '/home-valuation' },
    { label: 'Private Exclusives', path: '/private-exclusives' },
  ];

  const communitiesSubmenu = [
    { label: 'Coral Gables', path: '/coral-gables' },
    { label: 'Coconut Grove', path: '/coconut-grove' },
    { label: 'Miami Beach', path: '/miami-beach' },
    { label: 'Pinecrest', path: '/pinecrest' },
    { label: 'Key Biscayne', path: '/key-biscayne' },
    { label: 'South Miami', path: '/south-miami' },
    { label: 'Brickell', path: '/brickell' },
  ];

  const renderDropdown = (items: { label: string; path: string }[]) => (
    <div
      className="absolute top-full left-0 mt-0 z-50"
      style={{
        backgroundColor: '#181818',
        minWidth: '220px',
        paddingTop: '8px',
        paddingBottom: '8px',
        borderTop: '2px solid #eebc02',
        boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
      }}
      onMouseEnter={() => {
        if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
      }}
      onMouseLeave={handleMouseLeave}
    >
      {items.map((item, i) => (
        <Link
          key={i}
          to={item.path}
          className="dropdown-nav-item"
          style={DROPDOWN_ITEM_STYLE}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.color = '#eebc02';
            (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.05)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.color = '#ffffff';
            (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
          }}
        >
          {item.label}
        </Link>
      ))}
    </div>
  );

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{ backgroundColor: '#111010' }}
    >
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/8c6cdb48642edc833d50c52463daca7b/206ae5d05434646e4e52ed54a6c6d1a1.png"
              alt="Julian Acosta | Compass"
              className="h-16 md:h-14"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-9">

            {/* BUYERS */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter('buyers')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`${NAV_LINK} flex items-center gap-1 ${
                  isActiveGroup(['/buyers-guide', '/home-search', '/south-florida-relocation', '/relocate'])
                    ? 'text-[#eebc02]'
                    : ''
                }`}
              >
                Buyers
                <i className="ri-arrow-down-s-line text-sm"></i>
              </button>
              {openDropdown === 'buyers' && renderDropdown(buyersSubmenu)}
            </div>

            {/* SELLERS */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter('sellers')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`${NAV_LINK} flex items-center gap-1 ${
                  isActiveGroup(['/seller-guide', '/home-valuation', '/private-exclusives'])
                    ? 'text-[#eebc02]'
                    : ''
                }`}
              >
                Sellers
                <i className="ri-arrow-down-s-line text-sm"></i>
              </button>
              {openDropdown === 'sellers' && renderDropdown(sellersSubmenu)}
            </div>

            {/* COMMUNITIES */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter('communities')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`${NAV_LINK} flex items-center gap-1 ${
                  isActiveGroup(['/coral-gables', '/coconut-grove', '/miami-beach', '/pinecrest', '/key-biscayne', '/south-miami', '/brickell', '/communities'])
                    ? 'text-[#eebc02]'
                    : ''
                }`}
              >
                Communities
                <i className="ri-arrow-down-s-line text-sm"></i>
              </button>
              {openDropdown === 'communities' && renderDropdown(communitiesSubmenu)}
            </div>

            <Link
              to="/market-reports"
              className={`${NAV_LINK} ${isActive('/market-reports') ? 'text-[#eebc02]' : ''}`}
            >
              Market Reports
            </Link>

            <Link
              to="/about-julian"
              className={`${NAV_LINK} ${isActive('/about-julian') ? 'text-[#eebc02]' : ''}`}
            >
              About
            </Link>

            <Link
              to="/about-julian"
              className="px-5 py-3 font-montserrat font-semibold text-[14px] tracking-[0.08em] uppercase whitespace-nowrap cursor-pointer transition-all duration-300 text-white"
              style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.3)' }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#eebc02';
                (e.currentTarget as HTMLElement).style.color = '#111010';
                (e.currentTarget as HTMLElement).style.borderColor = '#eebc02';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#1a1a1a';
                (e.currentTarget as HTMLElement).style.color = '#ffffff';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.3)';
              }}
            >
              Consult
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-4">
            <a
              href="tel:7864732565"
              className="w-10 h-10 flex items-center justify-center text-white hover:text-[#eebc02] transition-colors duration-300 cursor-pointer"
              aria-label="Call Julian Acosta"
            >
              <i className="ri-phone-line text-xl"></i>
            </a>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="w-10 h-10 flex items-center justify-center text-white cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-[900px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="py-6 border-t border-white/20">
            <div className="space-y-5">

              {/* BUYERS mobile */}
              <div>
                <button
                  onClick={() => toggleMobileSubmenu('buyers')}
                  className={`w-full flex items-center justify-between ${MOBILE_NAV_LINK}`}
                >
                  <span>Buyers</span>
                  <i className={`ri-arrow-${openMobileSubmenu === 'buyers' ? 'up' : 'down'}-s-line`}></i>
                </button>
                {openMobileSubmenu === 'buyers' && (
                  <div className="mt-4 ml-4 space-y-4 border-l border-white/20 pl-5">
                    {buyersSubmenu.map((item, i) => (
                      <Link
                        key={i}
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={MOBILE_SUB_LINK}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* SELLERS mobile */}
              <div>
                <button
                  onClick={() => toggleMobileSubmenu('sellers')}
                  className={`w-full flex items-center justify-between ${MOBILE_NAV_LINK}`}
                >
                  <span>Sellers</span>
                  <i className={`ri-arrow-${openMobileSubmenu === 'sellers' ? 'up' : 'down'}-s-line`}></i>
                </button>
                {openMobileSubmenu === 'sellers' && (
                  <div className="mt-4 ml-4 space-y-4 border-l border-white/20 pl-5">
                    {sellersSubmenu.map((item, i) => (
                      <Link
                        key={i}
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={MOBILE_SUB_LINK}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* COMMUNITIES mobile */}
              <div>
                <button
                  onClick={() => toggleMobileSubmenu('communities')}
                  className={`w-full flex items-center justify-between ${MOBILE_NAV_LINK}`}
                >
                  <span>Communities</span>
                  <i className={`ri-arrow-${openMobileSubmenu === 'communities' ? 'up' : 'down'}-s-line`}></i>
                </button>
                {openMobileSubmenu === 'communities' && (
                  <div className="mt-4 ml-4 space-y-4 border-l border-white/20 pl-5">
                    {communitiesSubmenu.map((item, i) => (
                      <Link
                        key={i}
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={MOBILE_SUB_LINK}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/market-reports"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block ${MOBILE_NAV_LINK} ${isActive('/market-reports') ? 'text-[#eebc02]' : ''}`}
              >
                Market Reports
              </Link>

              <Link
                to="/about-julian"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block ${MOBILE_NAV_LINK} ${isActive('/about-julian') ? 'text-[#eebc02]' : ''}`}
              >
                About
              </Link>

              <Link
                to="/about-julian"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full px-6 py-3 font-montserrat font-semibold text-[15px] tracking-[0.08em] uppercase transition-all duration-300 text-center cursor-pointer whitespace-nowrap text-white"
                style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.3)' }}
              >
                Consult
              </Link>
            </div>
          </nav>
        </div>

      </div>
    </header>
  );
}
